import React, { useState } from 'react';
import { Avatar, Box, Collapse, Link, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';
import { ParticipantContactStatus, ParticipantFieldKind } from '@/api';
import { imageUrl } from '../participantApi';
import { ACCENT_SOFT, ACCENT_TEXT, BORDER, Card, GradientButton, LINK, MUTED, Pill, SoftColorButton, TextButton } from '../ui';

const initials = name =>
  (name || '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(s => s[0].toUpperCase())
    .join('');

// Аватар: фото или инициалы на мягком фоне (цвет стабилен для человека)
export const ParticipantAvatar = ({ card }) => (
  <Box sx={{ p: '1px', borderRadius: '50%', background: BORDER, flexShrink: 0 }}>
    <Avatar
      src={imageUrl(card.photo)}
      sx={{
        width: { xs: 48, sm: 56 },
        height: { xs: 48, sm: 56 },
        fontSize: { xs: 17, sm: 19 },
        border: '2px solid #fff',
        bgcolor: ACCENT_SOFT,
        color: ACCENT_TEXT,
        fontWeight: 700,
      }}
    >
      {initials(card.fullName)}
    </Avatar>
  </Box>
);

export const ContactList = ({ contacts }) => {
  if (!contacts || !contacts.length) {
    return (
      <Typography variant='body2' sx={{ color: MUTED }}>
        Участник не указал контактов.
      </Typography>
    );
  }
  return (
    <Box sx={{ display: 'grid', gap: 1 }}>
      {contacts.map(c => (
        <Box key={c.key}>
          <Typography sx={{ color: MUTED, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
            {c.label}
          </Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 500, whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
            {c.href ? (
              <Link href={c.href} target={c.href.startsWith('http') ? '_blank' : undefined} rel='noopener noreferrer'
                    underline='hover' sx={{ color: LINK }}>
                {c.value}
              </Link>
            ) : (
              c.value
            )}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

// Статус пары — бейдж в правом верхнем углу карточки (действий не требует)
const StatusPill = ({ status }) => {
  switch (status) {
    case ParticipantContactStatus.Requested:
      return <Pill>Запрос отправлен</Pill>;
    case ParticipantContactStatus.Declined:
      return <Pill>Запрос отклонён</Pill>;
    case ParticipantContactStatus.Incoming:
      return <Pill color='warning'>Входящий запрос</Pill>;
    case ParticipantContactStatus.Accepted:
      return (
        <Pill color='success'>
          <CheckIcon sx={{ fontSize: 16, mr: 0.5 }} />
          Контакт
        </Pill>
      );
    default:
      return null;
  }
};

// Действия по статусу — кнопки в нижней строке карточки
const Actions = ({ card, busy, onRequest, onAccept, onDecline }) => {
  switch (card.status) {
    case ParticipantContactStatus.None:
    case ParticipantContactStatus.DeclinedByMe:
      return onRequest ? (
        <GradientButton size='small' disabled={busy} onClick={() => onRequest(card)} sx={{ py: 0.8, fontSize: 14 }}>
          Запросить контакт
        </GradientButton>
      ) : null;
    case ParticipantContactStatus.Incoming:
      return (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <SoftColorButton tone='green' disabled={busy} onClick={() => onAccept(card)} sx={{ minWidth: 112 }}>
            Принять
          </SoftColorButton>
          <SoftColorButton tone='red' disabled={busy} onClick={() => onDecline(card)} sx={{ minWidth: 112 }}>
            Отклонить
          </SoftColorButton>
        </Box>
      );
    default:
      return null;
  }
};

const FieldValue = ({ field, kind }) => {
  if (kind === ParticipantFieldKind.MultiChoice || kind === ParticipantFieldKind.Choice) {
    return (
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.5 }}>
        {field.value
          .split(',')
          .map(s => s.trim())
          .filter(Boolean)
          .map(v => (
            <Pill key={v} color='accent'>
              {v}
            </Pill>
          ))}
      </Box>
    );
  }
  return (
    <Typography sx={{ fontSize: 14, whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
      {field.value}
    </Typography>
  );
};

// Анкета участника: сразу видно фото, имя, первые 2 поля, статус и действие;
// остальные поля — под «Подробнее» (первые 2 там не повторяем).
export const ParticipantItem = ({ card, kinds = {}, busy, onRequest, onAccept, onDecline, contactsOpen }) => {
  const [open, setOpen] = useState(false);
  const fields = card.fields || [];
  const subtitle = fields.slice(0, 2);
  const rest = fields.slice(2);
  const accepted = card.status === ParticipantContactStatus.Accepted;
  const requested = card.status === ParticipantContactStatus.Requested;
  const hasMore = rest.length > 0 || (accepted && !contactsOpen) || requested;
  const actions = <Actions card={card} busy={busy} onRequest={onRequest} onAccept={onAccept} onDecline={onDecline} />;
  const hasActions = [ParticipantContactStatus.None, ParticipantContactStatus.DeclinedByMe].includes(card.status)
    ? !!onRequest
    : card.status === ParticipantContactStatus.Incoming;

  return (
    <Card sx={{ p: { xs: 1.5, sm: 2 } }}>
      {/* тап по шапке карточки тоже раскрывает подробности */}
      <Box
        sx={{ display: 'flex', gap: 1.5, alignItems: 'center', cursor: hasMore ? 'pointer' : 'default' }}
        onClick={() => hasMore && setOpen(!open)}
      >
        <ParticipantAvatar card={card} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: { xs: 15, sm: 16 }, lineHeight: 1.3 }}>{card.fullName}</Typography>
          {subtitle.map((f, i) => (
            <Typography
              key={f.key}
              noWrap
              sx={{ color: MUTED, fontSize: 13, lineHeight: 1.4, fontStyle: i === 1 ? 'italic' : 'normal' }}
            >
              {f.value}
            </Typography>
          ))}
        </Box>
        {/* в «Контактах» бейдж «Контакт» не нужен — там все такие */}
        {!(contactsOpen && accepted) && (
          <Box sx={{ alignSelf: 'flex-start', flexShrink: 0, maxWidth: '45%' }}>
            <StatusPill status={card.status} />
          </Box>
        )}
      </Box>

      {(hasActions || hasMore) && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 1, flexWrap: 'wrap' }}>
          {hasActions && actions}
          {hasMore && (
            <TextButton
              size='small'
              onClick={() => setOpen(!open)}
              endIcon={<ExpandMoreIcon sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />}
              sx={{ ml: 'auto', px: 1, py: 0.25, fontSize: 13, minHeight: 0 }}
            >
              {open ? 'Свернуть' : 'Подробнее'}
            </TextButton>
          )}
        </Box>
      )}

      {contactsOpen && accepted && (
        <Box sx={{ mt: 1, p: 1.5, borderRadius: '12px', background: '#f7f8fa', border: `1px solid ${BORDER}` }}>
          <ContactList contacts={card.contacts} />
        </Box>
      )}

      <Collapse in={open} unmountOnExit>
        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid rgba(27,42,58,0.08)', display: 'grid', gap: 1.25 }}>
          {rest.map(f => (
            <Box key={f.key}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                {f.label}
              </Typography>
              <FieldValue field={f} kind={kinds[f.key]} />
            </Box>
          ))}
          {accepted && !contactsOpen && (
            <Box sx={{ p: 1.5, borderRadius: '12px', background: '#f7f8fa', border: `1px solid ${BORDER}` }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.75 }}>
                Контакты
              </Typography>
              <ContactList contacts={card.contacts} />
            </Box>
          )}
          {requested && (
            <Typography variant='body2' sx={{ color: MUTED }}>
              Контакты появятся, когда участник примет ваш запрос.
            </Typography>
          )}
        </Box>
      </Collapse>
    </Card>
  );
};
